import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  computeSimulation,
  validateConstantsForSave,
} from './priceSimulateCompute';
import PriceSummaryTables from './PriceSummaryTables';
import PriceSimulationBudgetCapture from './PriceSimulationBudgetCapture';
import {
  buildPriceSimulationMarkdown,
  savePriceSimulationBudget,
} from './priceSimulationBudgetExport';
import { roundMoney } from './budgetEngineV2';
import {
  loadInitialConstants,
  persistConstantsLocal,
  loadConstantsFromWorkspace,
  saveConstantsToWorkspace,
} from './priceSimulateStorage';
import { useTranslation } from '../../app/LanguageContext';
import { useWorkspace } from '../../app/WorkspaceContext';
import VariableCostsEditor from './VariableCostsEditor';
import SellingPriceFields, { applyQuantityChangeKeepingUnitPrice } from './SellingPriceFields';
import ProfitMarginFields from './ProfitMarginFields';
import {
  formatCurrency,
  formatNumberAsCurrencyMask,
  formatIntegerPercentInputFromNumber,
  handleCurrencyInputChange,
  handleIntegerPercentInputChange,
} from '../../app/currency';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

const selectInputTextOnFocus = (e) => {
  const el = e.target;
  queueMicrotask(() => {
    if (el && typeof el.select === 'function') {
      el.select();
    }
  });
};

const defaultProduct = () => ({
  description: '',
  quantity: 1,
  material: 'PLA',
  grams: 0,
  print_hours: 0,
  modeling_hours: 0,
  painting_hours: 0,
  variable_costs: [],
  selling_price_total: null,
  selling_price_unit: null,
});

const makeBlankVariableCostRows = (count) => {
  const base = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push({
      id: `vc-${base}-${i}-${Math.random().toString(36).slice(2, 11)}`,
      name: '',
      cost_unit: 0,
    });
  }
  return rows;
};

const stableStringifyConstants = (c) => {
  if (!c) return '';
  const mats = Object.keys(c.materials || {})
    .sort()
    .reduce((acc, k) => {
      acc[k] = c.materials[k];
      return acc;
    }, {});
  return JSON.stringify({ ...c, materials: mats });
};

const CurrencyAmountField = ({
  value,
  onChange,
  label,
  placeholder,
  size = 'small',
  fullWidth,
  sx,
  disabled,
  InputLabelProps,
}) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const num = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const shown = focused ? text : formatNumberAsCurrencyMask(num);

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      label={label}
      placeholder={placeholder}
      value={shown}
      disabled={disabled}
      InputLabelProps={InputLabelProps}
      onFocus={(e) => {
        setFocused(true);
        setText(formatNumberAsCurrencyMask(num));
        selectInputTextOnFocus(e);
      }}
      onBlur={() => {
        setFocused(false);
        setText('');
      }}
      onChange={(e) => {
        handleCurrencyInputChange(e, (synth) => {
          if (typeof synth === 'string') {
            setText('');
            onChange(0);
            return;
          }
          setText(synth.target.value);
          const nv = synth.target.numericValue;
          onChange(typeof nv === 'number' && Number.isFinite(nv) ? nv : 0);
        });
      }}
      sx={sx}
    />
  );
};

const PercentAmountField = ({
  value,
  onChange,
  label,
  size = 'small',
  fullWidth,
  sx,
  inputProps,
  disabled,
}) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const num = typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  const shown = focused ? text : formatIntegerPercentInputFromNumber(num);

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      label={label}
      value={shown}
      disabled={disabled}
      inputProps={inputProps}
      onFocus={(e) => {
        setFocused(true);
        setText(formatIntegerPercentInputFromNumber(num));
        selectInputTextOnFocus(e);
      }}
      onBlur={() => {
        const raw = text;
        setFocused(false);
        setText('');
        const digits = raw.replace(/\D/g, '');
        let n = digits === '' ? 0 : parseInt(digits, 10);
        if (!Number.isFinite(n) || n < 0) n = 0;
        onChange(n);
      }}
      onChange={(e) => {
        handleIntegerPercentInputChange(e, (synth) => {
          setText(synth.target.value);
          if (synth.target.value === '') {
            onChange(0);
            return;
          }
          if (typeof synth.numericValue === 'number' && Number.isFinite(synth.numericValue)) {
            onChange(Math.max(0, Math.round(synth.numericValue)));
          }
        });
      }}
      sx={sx}
    />
  );
};

const PriceSimulatePage = () => {
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const constantsRef = useRef(null);
  const budgetCaptureRef = useRef(null);
  const [saveBudgetMenuAnchor, setSaveBudgetMenuAnchor] = useState(null);
  const [savingBudget, setSavingBudget] = useState(false);
  const [constants, setConstants] = useState(() => loadInitialConstants());
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    stableStringifyConstants(loadInitialConstants()),
  );
  const [prefsHydrated, setPrefsHydrated] = useState(false);
  const [product, setProduct] = useState(() => {
    const c = loadInitialConstants();
    const keys = Object.keys(c.materials || {});
    return {
      ...defaultProduct(),
      material: keys.includes('PLA') ? 'PLA' : keys[0] || 'PLA',
      variable_costs: makeBlankVariableCostRows(5),
    };
  });
  const [result, setResult] = useState(null);
  const [simError, setSimError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  constantsRef.current = constants;

  const isDirty = useMemo(() => {
    if (!constants) return false;
    return stableStringifyConstants(constants) !== savedSnapshot;
  }, [constants, savedSnapshot]);

  const payloadProduct = useMemo(() => {
    const rows = Array.isArray(product.variable_costs) ? product.variable_costs : [];
    return {
      quantity: product.quantity,
      material: product.material,
      description: String(product.description ?? '').trim(),
      grams: nf(product.grams),
      print_hours: nf(product.print_hours),
      modeling_hours: nf(product.modeling_hours),
      painting_hours: nf(product.painting_hours),
      variable_costs: rows.map((r) => ({
        name: String(r.name || '').trim(),
        cost_unit: nf(r.cost_unit),
      })),
      selling_price_total: nf(product.selling_price_total) > 0 ? nf(product.selling_price_total) : null,
      selling_price_unit: nf(product.selling_price_unit) > 0 ? nf(product.selling_price_unit) : null,
    };
  }, [product]);

  const runSimulate = useCallback(() => {
    if (!constants) return;
    const r = computeSimulation(constants, payloadProduct);
    if (r.ok) {
      setResult(r.data);
      setSimError('');
    } else {
      setResult(null);
      setSimError(r.error || translate('common.error'));
    }
  }, [constants, payloadProduct, translate]);

  useEffect(() => {
    runSimulate();
  }, [runSimulate]);

  useEffect(() => {
    if (!currentWorkspace?.id) {
      setPrefsHydrated(true);
      return;
    }
    let cancelled = false;
    setPrefsHydrated(false);
    (async () => {
      try {
        const merged = await loadConstantsFromWorkspace(currentWorkspace.id);
        if (cancelled) return;
        const next = merged ?? loadInitialConstants();
        setConstants(next);
        setSavedSnapshot(stableStringifyConstants(next));
        setProduct((p) => {
          const keys = Object.keys(next.materials || {});
          const material = keys.includes(p.material) ? p.material : keys[0] || 'PLA';
          return { ...p, material };
        });
      } catch (_) {
        if (!cancelled) {
          const next = loadInitialConstants();
          setConstants(next);
          setSavedSnapshot(stableStringifyConstants(next));
        }
      } finally {
        if (!cancelled) setPrefsHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  const machineMaintenanceHour =
    constants?.machine != null ? nf(constants.machine.equipment_maintenance_per_hour) : 0;
  const machineDegradationHour =
    constants?.machine != null ? nf(constants.machine.equipment_degradation_per_hour) : 0;
  const machineEnergyHour =
    constants?.machine != null ? nf(constants.machine.energy_per_hour) : 0;
  const machineCostPerHour = constants?.machine != null
    ? roundMoney(machineMaintenanceHour + machineDegradationHour + machineEnergyHour)
    : 0;

  const companionRatePerHour =
    constants?.machine != null ? nf(constants.machine.print_labor_per_hour) : 0;

  const updateConstants = (path, value) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const materialEntries = useMemo(() => Object.entries(constants?.materials || {}), [constants]);

  const renameMaterial = (oldKey, newKey, cost) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      delete next.materials[oldKey];
      const name = newKey.trim() || oldKey;
      next.materials[name] = { cost_per_kg: nf(cost) };
      return next;
    });
    setProduct((p) => (p.material === oldKey ? { ...p, material: newKey.trim() || oldKey } : p));
  };

  const updateMaterialCost = (key, cost) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      if (next.materials[key]) next.materials[key].cost_per_kg = nf(cost);
      return next;
    });
  };

  const addMaterial = () => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      let i = 1;
      let name = `Material_${i}`;
      while (next.materials[name]) {
        i++;
        name = `Material_${i}`;
      }
      next.materials[name] = { cost_per_kg: 0 };
      return next;
    });
  };

  const removeMaterial = (key) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      delete next.materials[key];
      if (Object.keys(next.materials).length === 0) {
        next.materials.PLA = { cost_per_kg: 0 };
      }
      return next;
    });
    setProduct((p) => {
      if (p.material !== key) return p;
      const clone = { ...(constants.materials || {}) };
      delete clone[key];
      let keys = Object.keys(clone);
      if (keys.length === 0) keys = ['PLA'];
      return { ...p, material: keys[0] };
    });
  };

  const handleSave = () => {
    if (!constants) return;
    const active = document.activeElement;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }
    window.setTimeout(async () => {
      const current = constantsRef.current;
      if (!current) return;
      const err = validateConstantsForSave(current);
      if (err) {
        setSnackbar({ open: true, message: err, severity: 'error' });
        return;
      }
      try {
        persistConstantsLocal(current);
        if (currentWorkspace?.id) {
          await saveConstantsToWorkspace(currentWorkspace.id, current);
        }
        setSavedSnapshot(stableStringifyConstants(current));
        setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      } catch (e) {
        setSnackbar({
          open: true,
          message: e.message || String(e),
          severity: 'error',
        });
      }
    }, 0);
  };

  const handleCopySummary = useCallback(async () => {
    if (!result || !constants) return;
    const text = buildPriceSimulationMarkdown({
      translate,
      constants,
      payloadProduct,
      result,
    });
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: translate('price_simulate.copy_summary_success'),
        severity: 'success',
      });
    } catch (_) {
      setSnackbar({
        open: true,
        message: translate('price_simulate.copy_summary_error'),
        severity: 'error',
      });
    }
  }, [translate, constants, payloadProduct, result]);

  const handleSaveBudget = useCallback(
    async (format) => {
      if (!result || !constants || savingBudget) return;
      setSaveBudgetMenuAnchor(null);
      setSavingBudget(true);
      try {
        await savePriceSimulationBudget({
          disposition: 'download',
          format,
          translate,
          constants,
          payloadProduct,
          result,
          captureElement: budgetCaptureRef.current,
        });
        setSnackbar({
          open: true,
          message: translate('extensions.price_simulator.save_budget_success'),
          severity: 'success',
        });
      } catch (e) {
        setSnackbar({
          open: true,
          message: e?.message || translate('extensions.price_simulator.save_budget_error'),
          severity: 'error',
        });
      } finally {
        setSavingBudget(false);
      }
    },
    [translate, constants, payloadProduct, result, savingBudget],
  );

  if (!constants) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error">{translate('price_simulate.load_failed')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {translate('price_simulate.section_config')}
            </Typography>
            {isDirty && (
              <Alert severity="warning" sx={{ py: 0.5, mb: 1 }} variant="outlined">
                {translate('price_simulate.unsaved_hint')}
              </Alert>
            )}
            <Stack spacing={1} sx={{ mb: 0.5 }}>
              {materialEntries.map(([key, row]) => (
                <Stack key={key} direction="row" alignItems="flex-end" spacing={1}>
                  <TextField
                    size="small"
                    label={translate('price_simulate.material_name')}
                    defaultValue={key}
                    key={key}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onFocus={selectInputTextOnFocus}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== key) renameMaterial(key, v, row.cost_per_kg);
                    }}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <CurrencyAmountField
                    size="small"
                    label={translate('price_simulate.cost_per_kg')}
                    value={row.cost_per_kg}
                    onChange={(v) => updateMaterialCost(key, v)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 152, flexShrink: 0 }}
                  />
                  <IconButton
                    size="small"
                    sx={{ flexShrink: 0, mb: 0.5 }}
                    aria-label={translate('common.delete')}
                    onClick={() => removeMaterial(key)}
                    disabled={materialEntries.length <= 1}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddIcon />} onClick={addMaterial} sx={{ mt: 0.25 }}>
              {translate('price_simulate.add_material')}
            </Button>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.equipment_maintenance_hour')}
                value={constants.machine.equipment_maintenance_per_hour}
                onChange={(v) => updateConstants('machine.equipment_maintenance_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.equipment_degradation_hour')}
                value={constants.machine.equipment_degradation_per_hour}
                onChange={(v) => updateConstants('machine.equipment_degradation_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.energy_hour')}
                value={constants.machine.energy_per_hour}
                onChange={(v) => updateConstants('machine.energy_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.print_labor_hour')}
                value={constants.machine.print_labor_per_hour}
                onChange={(v) => updateConstants('machine.print_labor_per_hour', v)}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {translate('price_simulate.machine_cost_hour')}: {formatCurrency(machineCostPerHour)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate('price_simulate.companion_rate_hour')}: {formatCurrency(companionRatePerHour)}
            </Typography>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.modeling_hour')}
                value={constants.services.modeling_per_hour}
                onChange={(v) => updateConstants('services.modeling_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.painting_hour')}
                value={constants.services.painting_per_hour}
                onChange={(v) => updateConstants('services.painting_per_hour', v)}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.failure_pct')}
                value={constants.percentages.failure_percent}
                onChange={(v) => updateConstants('percentages.failure_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.marketing_pct')}
                value={constants.percentages.marketing_percent}
                onChange={(v) => updateConstants('percentages.marketing_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.reinvestment_pct')}
                value={constants.pricing.reinvestment_percent}
                onChange={(v) => updateConstants('pricing.reinvestment_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.profit_margin_pct')}
                value={constants.pricing.profit_margin_percent}
                onChange={(v) => updateConstants('pricing.profit_margin_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={!isDirty || (Boolean(currentWorkspace?.id) && !prefsHydrated)}
              >
                {translate('price_simulate.save_config')}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {translate('price_simulate.section_product')}
            </Typography>
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                label={translate('price_simulate.description')}
                value={product.description ?? ''}
                onFocus={selectInputTextOnFocus}
                onChange={(e) => setProduct((p) => ({ ...p, description: e.target.value }))}
                inputProps={{ maxLength: 2000 }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.quantity')}
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={product.quantity}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) =>
                    setProduct((p) => applyQuantityChangeKeepingUnitPrice(p, ni(e.target.value) || 1))
                  }
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id="price-sim-material-label">{translate('price_simulate.material')}</InputLabel>
                  <Select
                    labelId="price-sim-material-label"
                    label={translate('price_simulate.material')}
                    value={product.material}
                    onChange={(e) => setProduct((p) => ({ ...p, material: e.target.value }))}
                  >
                    {Object.keys(constants.materials || {}).map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.print_hours')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.print_hours}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, print_hours: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.grams')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.grams}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, grams: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block">
                {translate('price_simulate.print_hours_hint')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.modeling_hours')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.modeling_hours}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, modeling_hours: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.painting_hours')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.painting_hours}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, painting_hours: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Stack>

              <Divider sx={{ my: 0.5 }} />
              <VariableCostsEditor
                rows={product.variable_costs || []}
                onChange={(variable_costs) => setProduct((p) => ({ ...p, variable_costs }))}
              />

              <Divider sx={{ my: 0.5 }} />
              <SellingPriceFields
                product={product}
                onProductChange={setProduct}
                CurrencyFieldComponent={CurrencyAmountField}
              />
              <ProfitMarginFields
                product={product}
                result={result}
                onProductChange={setProduct}
                PercentFieldComponent={PercentAmountField}
                CurrencyFieldComponent={CurrencyAmountField}
              />
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                {translate('price_simulate.section_detailed')}
              </Typography>
              {result ? (
                <>
                  <Tooltip title={translate('price_simulate.copy_summary_tooltip')}>
                    <IconButton
                      size="small"
                      aria-label={translate('price_simulate.copy_summary_tooltip')}
                      onClick={handleCopySummary}
                      sx={{ color: 'text.secondary', opacity: 0.7 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={savingBudget}
                    startIcon={<SaveAltIcon sx={{ fontSize: '1rem !important' }} />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setSaveBudgetMenuAnchor(e.currentTarget)}
                    sx={{ ml: 0.5, py: 0.25, minWidth: 0, fontSize: '0.75rem' }}
                  >
                    {translate('extensions.price_simulator.save_budget')}
                  </Button>
                  <Menu
                    anchorEl={saveBudgetMenuAnchor}
                    open={Boolean(saveBudgetMenuAnchor)}
                    onClose={() => setSaveBudgetMenuAnchor(null)}
                  >
                    <MenuItem disabled={savingBudget} onClick={() => handleSaveBudget('png')}>
                      {translate('extensions.price_simulator.save_budget_png')}
                    </MenuItem>
                    <MenuItem disabled={savingBudget} onClick={() => handleSaveBudget('markdown')}>
                      {translate('extensions.price_simulator.save_budget_markdown')}
                    </MenuItem>
                  </Menu>
                </>
              ) : null}
            </Stack>
            {simError && (
              <Alert severity="error" sx={{ py: 0.5, mb: 1 }} variant="outlined">
                {simError}
              </Alert>
            )}
            {result && (
              <Stack spacing={0.5}>
                {payloadProduct.description ? (
                  <>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {translate('price_simulate.description')}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', opacity: 0.92 }}>
                        {payloadProduct.description}
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 0.35 }} />
                  </>
                ) : null}

                <PriceSummaryTables
                  translate={translate}
                  result={result}
                  payloadProduct={payloadProduct}
                />
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {result ? (
        <PriceSimulationBudgetCapture
          ref={budgetCaptureRef}
          translate={translate}
          result={result}
          payloadProduct={payloadProduct}
        />
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PriceSimulatePage;
